import type { HeldSpaceSummary } from '@models/api';
import { QuestionVisibility } from '@models/api/question';
import { VISIBILITY_OPTION_TITLE } from '@components/create-question/visibilityOptionTitles';
import { ISOLATE_CONTROLS, isolated } from './bidi';
import { cutMiddle, isListWord } from './graphemes';
import { spaceCount } from './spaceCount';
import {
  isSpaceScopedVisibility,
  pendingHeldSpaces,
  type CrosspostOutcome,
  type HeldPostOutcome,
} from './heldSpaces';

/**
 * User-facing strings for held posts and the Space-required alert. Reviewed against
 * meetball-tone-of-voice: second person, one breath, no exclamation marks, no em dashes.
 */

/** Inline role="alert" in the Spaces drawer when Post is tapped with nothing to post to. */
export const SPACE_REQUIRED_ALERT =
  'Only Space members can see this post. Pick a Space, or change who can see it.';

/**
 * Inline role="alert" in the Spaces drawer when an edit is held back for a Space: the save would
 * write who can read the post, is not for Anyone, has Events, and names no Space
 * (editMustNameSpaces). It names the option the post is on, in the selector's own words, and
 * what it needs. Without it Save only reopens the picker.
 */
// AIDEV-TODO: reword the hint for My network before featureFlags.networkVisibility is on
export const editNeedsSpaceAlert = (visibility: QuestionVisibility): string =>
  `“${
    VISIBILITY_OPTION_TITLE[
      visibility === QuestionVisibility.my_network
        ? QuestionVisibility.my_network
        : QuestionVisibility.space_only
    ]
  }” needs a Space. Pick one.`;

/**
 * What a post made from an Event needs while none of that Event's host Spaces is ticked. One line
 * in three places: the composer's submit button, the hint above the confirm drawer's Post, and,
 * once a tap has been stopped for it, the inline role="alert" in the Spaces drawer.
 */
export const PICK_HOST_SPACE = 'Pick a host Space';

/**
 * A post for someone else has to be live somewhere: when every Space it is addressed to is one
 * the author is only PENDING in, it would only be held, and the backend refuses it
 * (400 ASK_ON_BEHALF_HELD_ONLY). The composer stops a new post first with this inline alert.
 * It says what the post needs and the two ways to get there; it does not say what was refused.
 */
export const ASK_ON_BEHALF_HELD_ONLY_ALERT =
  "A post for someone else needs a Space you're already in. Pick one, or post it for yourself.";

/**
 * The same refusal on an edit, where only the server can raise it. An edit cannot change who
 * the post is for, so the one way out is another Space.
 */
export const ASK_ON_BEHALF_HELD_ONLY_ON_EDIT =
  "A post for someone else needs a Space you're already in. Pick one and save again.";

/** Error toast when the server refuses a post for someone else that would only be held. */
export const askOnBehalfHeldOnlyToast = ({ isEdit }: { isEdit: boolean }) => ({
  title: "That didn't go through",
  description: isEdit ? ASK_ON_BEHALF_HELD_ONLY_ON_EDIT : ASK_ON_BEHALF_HELD_ONLY_ALERT,
});

/**
 * Error toast when the server refuses a post over Spaces it can no longer go to: a 403 naming
 * Spaces the author can no longer post in (`details.forbidden_space_ids`: a request that was
 * declined or withdrawn, a membership that was removed), or a 404 naming Spaces that are gone
 * (`details.missing_space_ids`: deleted, archived, or the author was blocked there). The
 * composer has already taken those Spaces off the selection. It names no Space and no cause:
 * by then the Space is gone from the author's list, and its name with it, and the one sentence
 * has to be true of every cause. Worded for a new post and for an edit alike.
 */
export const forbiddenSpacesToast = (count: number) => ({
  title: "That didn't go through",
  description:
    count > 1
      ? 'Some Spaces you picked are no longer on your list. Check where your post is going and try again.'
      : 'A Space you picked is no longer on your list. Check where your post is going and try again.',
});

/**
 * How much of a long name a sentence shows (TECH-718, `cutMiddle`): it keeps at most 32
 * user-perceived characters, up to 16 of them from its end, and adds "\u2026 ", so a cut name shows up
 * to 34. Three long names then stay a few lines in a toast on a phone, and the chapters of one
 * community, which differ at the end, still read apart.
 */
const NAME_KEEP = 32;
const NAME_END_KEEP = 16;

/**
 * A name as a sentence holds it before any cut: its isolate controls dropped, NFC (so "Caf\u00e9"
 * typed either way is one name), and its whitespace collapsed. Two names equal in this form are
 * the same name; any other two are different Spaces, however alike they look.
 */
const nameInSentence = (name: string): string =>
  name.replace(ISOLATE_CONTROLS, '').normalize('NFC').replace(/\s+/g, ' ').trim();

/**
 * Whether `name` is one a sentence can show: a string that is not empty as a name in a sentence
 * (`nameInSentence`). A name slot can hold anything a response drifted to (null, a number, an
 * object), and the types cannot stop that, so it is checked here, where every name passes. The
 * one test of it: the held labels below and the Event gate's Waiting title leave out a name it
 * says no to.
 */
export const canShowName = (name: unknown): name is string =>
  typeof name === 'string' && nameInSentence(name) !== '';

/**
 * Whether a sentence can name all `count` places from `names`: a name for each, and each one it
 * can show (`canShowName`). A name it cannot show is a missing name, and a sentence that misses a
 * name counts its places instead, so it never names a partial set and never prints a name that is
 * not there. `formatSpaceNameLists` counts every list this is false for, and the share drawer's
 * sentences, which count in their own words, ask it before they name (shareToSpacesCopy).
 */
export const namesEvery = (count: number, names: readonly unknown[]): boolean =>
  names.length === count && names.every(canShowName);

/**
 * Characters that show nothing (Default_Ignorable_Code_Point: a zero-width space, a joiner, a
 * variation selector). Left out when two names are compared, and only then: what is shown keeps
 * them, so an emoji built with joiners still renders.
 */
const SHOWS_NOTHING = /\p{DI}/gu;

/** Two shown names read alike when they differ at most in case and in what shows nothing. */
const readAlike = (one: string, other: string): boolean =>
  one.replace(SHOWS_NOTHING, '').toLowerCase() === other.replace(SHOWS_NOTHING, '').toLowerCase();

/**
 * Every name one toast or hint shows, from all of its lists at once, each cut to `NAME_KEEP`
 * (`cutMiddle`), except names that would cut alike. Different names must never read alike
 * anywhere in one toast or hint, and the reader must see what tells them apart, so a different
 * name whose cut reads like another's (case aside) is shown in full, and so is the other. Names
 * that cut apart keep their cut, and the same name in two lists is one name, not a pair. It
 * settles: each pass turns at least one cut whole, and a name shown whole is never cut again.
 */
const cutApart = (names: readonly string[]): string[] => {
  const shown = names.map(name => cutMiddle(name, { keep: NAME_KEEP, keepEnd: NAME_END_KEEP }));
  const cutsReadingAlike = () =>
    shown.map(
      (form, index) =>
        form !== names[index] &&
        shown.some((other, at) => names[at] !== names[index] && readAlike(other, form)),
    );
  let toShowWhole = cutsReadingAlike();
  while (toShowWhole.includes(true)) {
    toShowWhole.forEach((whole, index) => {
      if (whole) shown[index] = names[index];
    });
    toShowWhole = cutsReadingAlike();
  }
  return shown;
};

/**
 * A name that reads as a list of its own, or as two names: it has the word "and" or "or", "&",
 * "+", a comma, or a hyphen, en dash or em dash with a space on each side (Dexter, 2026-10-18).
 * Its words are the runs of Unicode letters, marks and digits between the rest, so "Señor" and
 * "Ñandú" hold no "or" or "and"; a word is "and" or "or" by `isListWord`, the one test a cut also
 * drops a hanging one by. No lookbehind, which Safari has only from 16.4, while iOS builds go back
 * to 16.0. An unspaced hyphen ("Hand-made") is part of a word.
 */
const LIST_MARK = /[&+,]| [-\u2013\u2014] /u;
const BETWEEN_WORDS = /[^\p{L}\p{M}\p{N}]+/u;
const nameReadsAsAList = (name: string): boolean =>
  LIST_MARK.test(name) || name.split(BETWEEN_WORDS).some(isListWord);

/**
 * The plural a list counts places with, in its sentence's register: "2 Spaces called Berlin Tech"
 * in the composer, "2 spaces called Berlin Tech" in the share drawer, Events in the crosspost
 * toast. A list it cannot name in full is counted in it too ("2 Spaces", `countOf`).
 */
export type SameNameNoun = 'Spaces' | 'spaces' | 'Events';

/**
 * A list said as a count, for a sentence that cannot name every place in it (`namesEvery`): "1
 * Space", "2 spaces", "2 Events", in the list's register. Spaces are counted by `spaceCount`.
 */
const countOf = (count: number, noun: SameNameNoun): string => {
  if (noun === 'Events') return `${count} ${count === 1 ? 'Event' : 'Events'}`;
  return spaceCount(count, noun === 'Spaces' ? 'sentence' : 'lowercase');
};

/** A list of names that a toast or a hint shows (`formatSpaceNameLists`). */
export interface NameList {
  names: readonly string[];
  /** The plural that same-name places are counted with ("2 Spaces called X"): Spaces by default. */
  noun?: SameNameNoun;
  /**
   * How many names it says before it counts the rest. Three by default: "A, B and C", then "A, B
   * and 2 more". The Going toasts say one: "A and 2 more".
   */
  namedAtMost?: number;
}

/**
 * A list's names before any cut: a name several Spaces share once with its count (only the same
 * name, `nameInSentence`, never two names a cut makes alike), the names it says, and how many it
 * folds into "N more". Every name is said while they fit in `namedAtMost`. Past that it says one
 * name fewer and counts the rest, and always says at least one: three gives "A, B and 2 more" for
 * four names, one gives "A and 1 more" for two.
 */
const namedOf = ({ names, namedAtMost = 3 }: NameList) => {
  const groups: { name: string; count: number }[] = [];
  for (const name of names.map(nameInSentence)) {
    const group = groups.find(candidate => candidate.name === name);
    if (group) group.count += 1;
    else groups.push({ name, count: 1 });
  }
  const named =
    groups.length <= namedAtMost ? groups : groups.slice(0, Math.max(1, namedAtMost - 1));
  const moreCount = names.length - named.reduce((total, group) => total + group.count, 0);
  return { named, moreCount };
};

const anyReadsAsAList = (names: readonly string[]) => names.some(nameReadsAsAList);

/** The pairs a name can come in quotes of its own with: curly or straight. */
const OWN_QUOTE_PAIRS = [
  ['“', '”'],
  ['"', '"'],
] as const;

/**
 * A name that comes in quotes of its own, curly or straight: it gets no second pair. Only one
 * quoted string counts, one pair around all of it with no quote of the same kind inside:
 * `"Foo" and "Bar"` is two quoted parts, and without a pair of its own it reads as two names.
 */
const inQuotesAlready = (name: string): boolean =>
  OWN_QUOTE_PAIRS.some(([open, close]) => {
    if (name.length < 2 || !name.startsWith(open) || !name.endsWith(close)) return false;
    const inside = name.slice(open.length, name.length - close.length);
    return !inside.includes(open) && !inside.includes(close);
  });

/**
 * The lists of Spaces (or Events) that one toast or hint names (TECH-718), formatted together, one
 * string per list: "A", "A and B", "A, B and C", then "A, B and 2 more" from four on (`NameList`).
 * Every surface that names places in a sentence lists them through this, so each name is dressed
 * the same way everywhere:
 * - cut in the middle when long, keeping its end. No two different names read alike anywhere in
 *   the toast or hint, across its lists too: names that would cut alike are shown in full
 *   (`cutApart`);
 * - in its own isolate, so a right-to-left name keeps its order;
 * - said once with a count when several Spaces share it, "2 Spaces called Berlin Tech", in `noun`;
 * - quoted, every name of the sentence ("“Coffee and Code” and “Lisbon”"), once a name it shows
 *   reads as a list of its own. Quotes are decided per sentence: each list on its own, or all the
 *   lists at once with `oneSentence` when they sit in one sentence. A name already in quotes, curly
 *   or straight, gets no second pair.
 *
 * A list with a name it cannot show (not a string, or empty: `namesEvery`) is counted in its
 * `noun` instead, "2 Spaces", never named in part. So no response, however it drifted, makes a
 * sentence throw or print "null".
 */
export const formatSpaceNameLists = (
  lists: readonly NameList[],
  { oneSentence = false }: { oneSentence?: boolean } = {},
): string[] => {
  // A counted list shows no name, so none of its names takes part in the cut or the quotes.
  const counted = lists.map(({ names }) => !namesEvery(names.length, names));
  const nameds: ReturnType<typeof namedOf>[] = lists.map((list, index) =>
    counted[index] ? { named: [], moreCount: 0 } : namedOf(list),
  );
  const cuts = cutApart(nameds.flatMap(({ named }) => named.map(group => group.name)));
  let offset = 0;
  const shown = nameds.map(({ named, moreCount }) => {
    const groups = named.map((group, index) => ({
      name: cuts[offset + index],
      count: group.count,
    }));
    offset += named.length;
    return { groups, moreCount, readsAsAList: anyReadsAsAList(groups.map(group => group.name)) };
  });
  const sentenceQuoted = shown.some(list => list.readsAsAList);
  return shown.map(({ groups, moreCount, readsAsAList }, index) => {
    const noun = lists[index].noun ?? 'Spaces';
    if (counted[index]) return countOf(lists[index].names.length, noun);
    const quoted = oneSentence ? sentenceQuoted : readsAsAList;
    const items = groups.map(({ name, count }) => {
      const said = quoted && !inQuotesAlready(name) ? `“${isolated(name)}”` : isolated(name);
      return count > 1 ? `${count} ${noun} called ${said}` : said;
    });
    if (moreCount > 0) items.push(`${moreCount} more`);
    if (items.length <= 1) return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  });
};

/** One list of names, alone in its toast or hint: `formatSpaceNameLists` with one list. */
export const formatSpaceNameList = (
  names: readonly string[],
  shape: Omit<NameList, 'names'> = {},
): string => formatSpaceNameLists([{ ...shape, names }])[0];

/**
 * The held-post promise for `spaceCount` Spaces, one host per Space: "once the host approves your
 * request", or "once the hosts approve your requests" for more than one. Its one spelling: the
 * composer's held copy below and the share drawer's (shareToSpacesCopy) both say it through this,
 * so a wording change reaches every surface at once. The crossposted toast's "once a host approves
 * your request" is a different promise (the first of several approvals publishes the Event).
 */
export const onceApprovedClause = (spaceCount: number): string =>
  spaceCount > 1 ? 'once the hosts approve your requests' : 'once the host approves your request';

/**
 * The line above Post in the composer's confirm drawer: where the post lands when it is tapped.
 * `liveNames` are the selected places the post reaches at once (Spaces the author is a member
 * of), `pendingNames` the selected Spaces the author has only asked to join. A pending Space
 * is never named as a place the post is going to now: it is named as where the post appears
 * once the host approves. With a Space-scoped visibility (SPACE_ONLY or SPACE_WIDE, which the
 * composer labels alike) the selected Spaces are the whole audience, and with nothing selected
 * yet the line asks for a pick. With any other visibility the Spaces are added to the audience.
 */
export const confirmPostHint = ({
  visibility,
  liveNames,
  pendingNames,
}: {
  visibility: QuestionVisibility;
  liveNames: readonly string[];
  pendingNames: readonly string[];
}): string => {
  // One hint, two sentences: the names are cut together, so none reads like another, and each
  // sentence decides its own quotes.
  const [live, pending] = formatSpaceNameLists([{ names: liveNames }, { names: pendingNames }]);
  const onceApproved = onceApprovedClause(pendingNames.length);

  if (isSpaceScopedVisibility(visibility)) {
    if (liveNames.length === 0 && pendingNames.length === 0) {
      return 'Tap to select where you want to post';
    }
    if (pendingNames.length === 0) return `Posting to ${live}`;
    if (liveNames.length === 0) return `Your post goes live in ${pending} ${onceApproved}`;
    return `Posting to ${live}. It'll also appear in ${pending} ${onceApproved}.`;
  }

  const audience =
    visibility === QuestionVisibility.my_network
      ? 'Posting to your network'
      : 'Posting to everyone on Meetball';
  const postingTo = liveNames.length === 0 ? audience : `${audience} and ${live}`;
  return pendingNames.length === 0
    ? postingTo
    : `${postingTo}. It'll also appear in ${pending} ${onceApproved}.`;
};

/** After a create where nothing is live yet. */
export const heldOnlyToast = (names: readonly string[]) => ({
  title: names.length > 1 ? 'Waiting on the hosts' : 'Waiting on the host',
  description: `Your post goes live in ${formatSpaceNameList(names)} ${onceApprovedClause(names.length)}.`,
});

/** After a create that is live somewhere and also held somewhere. */
export const mixedToast = (names: readonly string[]) => ({
  title: 'Posted',
  description: `It'll also appear in ${formatSpaceNameList(names)} ${onceApprovedClause(names.length)}.`,
});

/**
 * After an edit is saved, from what the server says the save left held (see
 * `classifyHeldPostOutcome`). A save can itself leave a post waiting: picking only a Space the
 * author is pending in takes a live post out of sight until a host approves. The toast says
 * where the post goes live and when, in one sentence; it does not list what stopped. Callers
 * keep a toast with a description up longer than the default.
 */
export const postUpdatedToast = (
  outcome: HeldPostOutcome,
): { title: string; description?: string } => {
  if (outcome.kind === 'live') return { title: 'Post updated' };
  const names = outcome.held.map(space => space.name);
  const list = formatSpaceNameList(names);
  const when = `${onceApprovedClause(names.length)}.`;
  return {
    title: 'Post updated',
    description:
      outcome.kind === 'held_only'
        ? `Your post goes live in ${list} ${when}`
        : `It'll also appear in ${list} ${when}`,
  };
};

/**
 * After the crosspost dialog saves, from what the server says the save did with the chosen
 * Events (see `classifyCrosspostOutcome`): where the post is now, and, when an Event waits,
 * where it will appear once a host approves. "A host", not "the host": an Event can wait with
 * more than one held Space, and the first approval publishes it. When every chosen Event waits
 * nothing was crossposted yet, and the title does not say it was. With no Event to name in
 * either list the sentence names none, and says only what a save that went through makes true.
 * Callers keep it up longer than the default: it always has a second line.
 */
export const crosspostedToast = ({
  linked,
  waiting,
}: CrosspostOutcome): { title: string; description: string } => {
  if (linked.length === 0 && waiting.length === 0) {
    return { title: 'Post updated', description: 'The Events you picked are saved.' };
  }
  // One toast, two sentences: the Event names are cut together, so none reads like another, and
  // each sentence decides its own quotes.
  const [linkedList, waitingList] = formatSpaceNameLists([
    { names: linked, noun: 'Events' },
    { names: waiting, noun: 'Events' },
  ]);
  const onceApproved = `in ${waitingList} once a host approves your request.`;
  if (linked.length === 0) {
    return { title: 'Waiting on a host', description: `It'll appear ${onceApproved}` };
  }
  const now = `It's now in ${linkedList}.`;
  return {
    title: 'Post crossposted',
    description: waiting.length === 0 ? now : `${now} It'll also appear ${onceApproved}`,
  };
};

/**
 * The author's empty state on the page of a post that is live nowhere (see `isHeldOnlyPost`),
 * in place of the invitation to share it: nobody else can open the link yet. While a host has
 * still to decide it says what happens next; once every request was declined it says what the
 * author can do, and carries the label of the button that does it (`editLabel`).
 */
export const heldOnlyEmptyState = (
  heldSpaces: readonly HeldSpaceSummary[],
): { heading: string; body: string; editLabel?: string } => {
  const waitingOn = pendingHeldSpaces(heldSpaces).map(space => space.name);
  if (waitingOn.length === 0) {
    return {
      heading: "This post isn't in a Space yet",
      body: "Edit it and pick a Space you're in.",
      editLabel: 'Edit post',
    };
  }
  return {
    heading: waitingOn.length > 1 ? 'Waiting on the hosts' : 'Waiting on the host',
    body: `Your post goes live in ${formatSpaceNameList(waitingOn)} ${onceApprovedClause(waitingOn.length)}. You can share it then.`,
  };
};

/**
 * The author's status line for one held Space link (badge text and card label). A name it cannot
 * show (`canShowName`, as a sentence decides it) is left out, never printed as "null": the pending
 * line is then "Pending approval", and the declined one "Not posted in one Space": "a Space" could
 * read as "in no Space at all", and the post may well be live in another.
 */
export const heldStatusLabel = (space: HeldSpaceSummary): string => {
  const name = canShowName(space.name) ? space.name : null;
  if (space.status === 'declined') {
    return name === null ? 'Not posted in one Space' : `Not posted in ${name}`;
  }
  return name === null ? 'Pending approval' : `Pending approval · ${name}`;
};
